import { ArrowLeft, Copy, FileDown, Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
	buildPaymentPayloadFromContract,
	getPublicContractBuyerInfo,
	getPublicContractBuyerName,
	getPublicContractCode,
	getPublicContractItemName,
	getPublicContractItems,
	getPublicContractPaymentKey,
	getPublicContractTotal,
	readStashedPublicInvoicePayment,
	stashPublicInvoicePayment,
	useCancelPublicContract,
	useInitiatePublicContractPayment,
	usePublicContract,
	useVerifyPublicContractPayment
} from "@/api/public-contract-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { shareOrDownloadInvoicePdf } from "@/lib/invoice-pdf";
import { cn, formatCurrency } from "@/lib/utils";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor/invoices/$invoiceid/")({
	component: RouteComponent
});

const STATUS_LABELS: Record<string, string> = {
	PENDING: "Pending",
	PAID: "Paid",
	SHIPED: "Shipped",
	COMPLETED: "Completed",
	ABANDONED: "Abandoned",
	EXPIRED: "Expired",
	CANCELED: "Canceled"
};

function statusClass(statusKey: string) {
	if (statusKey === "PAID")
		return "bg-blue-100 text-blue-800";
	if (statusKey === "PENDING")
		return "bg-yellow-100 text-yellow-800";
	if (statusKey === "CANCELED")
		return "bg-red-100 text-red-800";
	return "bg-gray-100 text-gray-700";
}

function RouteComponent() {
	const { invoiceid } = Route.useParams();
	const { data: contract, isPending, isError, refetch, isFetching } = usePublicContract(invoiceid);
	const cancelInvoice = useCancelPublicContract();
	const verifyPayment = useVerifyPublicContractPayment();
	const initiatePayment = useInitiatePublicContractPayment();
	const [payment, setPayment] = useState(() => readStashedPublicInvoicePayment(invoiceid));
	const [sharingPdf, setSharingPdf] = useState(false);
	const autoFetchTried = useRef(false);

	const items = useMemo(() => getPublicContractItems(contract), [contract]);
	const code = getPublicContractCode(contract);
	const total = getPublicContractTotal(contract);
	const settlement = payment?.paymentConfig?.settlementAccount;
	const paymentRef
		= payment?.paymentRef
		|| payment?.order?.paymentRef
		|| contract?.paymentRef
		|| payment?.paymentConfig?.reference
		|| "";

	const buyer = getPublicContractBuyerInfo(contract);
	const buyerName = getPublicContractBuyerName(contract) || "-";
	const amountToPay = Number(payment?.order?.orderAmount ?? total ?? 0) || 0;

	const copyText = async (value: string, label: string) => {
		if (!value) {
			toast.error(`Nothing to copy for ${label}`);
			return;
		}
		try {
			await navigator.clipboard.writeText(value);
			toast.success(`${label} copied`);
		}
		catch {
			toast.error("Copy failed");
		}
	};

	const shareInvoicePdf = async () => {
		if (!contract) {
			toast.error("Invoice is still loading");
			return;
		}
		setSharingPdf(true);
		try {
			const result = await shareOrDownloadInvoicePdf({
				title: contract.title || "Invoice",
				code,
				id: contract.id,
				status: STATUS_LABELS[String(contract.status || "").toUpperCase()] || String(contract.status || ""),
				createdAt: contract.createdAt,
				buyerName,
				buyerEmail: buyer?.email ? String(buyer.email) : undefined,
				buyerPhone: buyer?.phoneNumber || buyer?.phone
					? String(buyer.phoneNumber || buyer.phone)
					: undefined,
				buyerAddress: buyer?.buyerAddress ? String(buyer.buyerAddress) : undefined,
				description: contract.description,
				items: items.map((item) => {
					const qty = Number(item.quantity ?? item.productQuantity ?? 0) || 0;
					const unit = Number(item.vendorDiscountedPrice ?? item.price ?? 0) || 0;
					const line = item.totalCost != null ? Number(item.totalCost) : qty * unit;
					return {
						name: getPublicContractItemName(item),
						qty,
						unit,
						line
					};
				}),
				deliveryCost: contract.deliveryCost != null ? Number(contract.deliveryCost) || 0 : null,
				total: Number(total ?? amountToPay) || 0,
				bankName: settlement?.bankName,
				accountName: settlement?.accountName,
				accountNumber: settlement?.accountNumber,
				amountToPay,
				paymentRef: paymentRef || undefined
			});
			if (result === "shared")
				toast.success("Invoice PDF shared");
			else if (result === "downloaded")
				toast.success("Invoice PDF downloaded");
		}
		catch {
			toast.error("Could not create the invoice PDF");
		}
		finally {
			setSharingPdf(false);
		}
	};

	const refreshBankDetails = async (silent = false) => {
		if (!contract)
			return;
		const payload = buildPaymentPayloadFromContract(contract);
		if (!payload) {
			if (!silent)
				toast.error("Buyer email is missing — cannot load payment details");
			return;
		}
		try {
			const next = await initiatePayment.mutateAsync({
				contractId: getPublicContractPaymentKey(contract),
				payload
			});
			stashPublicInvoicePayment(contract.id, next);
			setPayment(next);
			if (!silent)
				toast.success("Payment details ready");
		}
		catch {
			/* toast in mutation */
		}
	};

	useEffect(() => {
		autoFetchTried.current = false;
		setPayment(readStashedPublicInvoicePayment(invoiceid));
	}, [invoiceid]);

	useEffect(() => {
		if (!contract || settlement?.accountNumber || autoFetchTried.current)
			return;
		if (!buildPaymentPayloadFromContract(contract))
			return;
		autoFetchTried.current = true;
		void refreshBankDetails(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contract?.id, settlement?.accountNumber]);

	if (isPending && !contract) {
		return (
			<div className="min-h-[50vh] grid place-content-center">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
			</div>
		);
	}

	if (isError || !contract) {
		return (
			<div className="container mx-auto max-w-3xl space-y-4 px-4 py-10">
				<p className="text-muted-foreground">
					{isError ? "Could not load this invoice. Check your connection and try again." : "Invoice not found."}
				</p>
				<div className="flex flex-wrap gap-2">
					{isError
						? (
								<Button type="button" onClick={() => void refetch()} disabled={isFetching}>
									{isFetching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
									Try again
								</Button>
							)
						: null}
					<Button asChild variant="outline">
						<Link to="/vendor/invoices">Back to invoices</Link>
					</Button>
				</div>
			</div>
		);
	}

	const statusKey = String(contract.status || "").toUpperCase();

	return (
		<section className="mx-auto w-full max-w-3xl space-y-4 pb-8 sm:space-y-6 sm:pb-16">
			<div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-start sm:justify-between sm:pt-4">
				<div className="flex min-w-0 items-start gap-2">
					<Button asChild variant="ghost" size="icon" className="mt-0.5 shrink-0">
						<Link to="/vendor/invoices">
							<ArrowLeft className="h-4 w-4" />
						</Link>
					</Button>
					<div className="min-w-0 flex-1">
						<div className="flex items-start justify-between gap-2">
							<h1 className="text-lg font-semibold leading-snug break-words sm:text-xl">
								{contract.title || "Invoice"}
							</h1>
							<span className={cn("shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold sm:hidden", statusClass(statusKey))}>
								{STATUS_LABELS[statusKey] || contract.status || "-"}
							</span>
						</div>
						<p className="mt-1 truncate text-xs text-muted-foreground sm:text-sm">
							{code ? `Code ${code}` : contract.id}
						</p>
					</div>
				</div>
				<div className="flex w-full items-center gap-2 sm:w-auto">
					<span className={cn("hidden px-3 py-1 rounded-full text-xs font-semibold sm:inline-flex", statusClass(statusKey))}>
						{STATUS_LABELS[statusKey] || contract.status || "-"}
					</span>
					<Button
						type="button"
						className="w-full sm:w-auto"
						onClick={() => void shareInvoicePdf()}
						disabled={sharingPdf}
					>
						{sharingPdf
							? <Loader2 className="h-4 w-4 animate-spin mr-2" />
							: <FileDown className="h-4 w-4 mr-2" />}
						Share Invoice
					</Button>
				</div>
			</div>

			<Card className="border-violet-100 bg-violet-50">
				<CardHeader className="p-4 pb-2 sm:p-6 sm:pb-2">
					<CardTitle className="text-base">Bank transfer details</CardTitle>
					<p className="text-sm text-muted-foreground">
						Copy these details for the buyer. Use Share Invoice for the full PDF.
					</p>
				</CardHeader>
				<CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
					{settlement?.accountNumber
						? (
								<div className="space-y-4 rounded-lg bg-white p-3 sm:p-5">
									<CopyableField
										label="Bank"
										value={settlement.bankName || "SafeHaven Microfinance Bank"}
										onCopy={copyText}
									/>
									<CopyableField
										label="Account name"
										value={settlement.accountName || "Intasida"}
										onCopy={copyText}
									/>
									<CopyableField
										label="Account number"
										value={settlement.accountNumber}
										emphasis
										onCopy={copyText}
									/>
									<CopyableField
										label="Amount"
										value={formatCurrency(amountToPay, "NGN")}
										copyValue={String(amountToPay)}
										emphasis
										onCopy={copyText}
									/>
									{paymentRef
										? (
												<CopyableField
													label="Payment reference"
													value={String(paymentRef)}
													mono
													onCopy={copyText}
												/>
											)
										: null}
								</div>
							)
						: (
								<div className="space-y-3 rounded-lg border border-dashed bg-white p-4 text-center sm:p-6">
									<p className="text-sm text-muted-foreground">
										{initiatePayment.isPending
											? "Loading bank details…"
											: "No bank details loaded yet for this session."}
									</p>
									<Button
										type="button"
										className="w-full sm:w-auto"
										onClick={() => refreshBankDetails(false)}
										disabled={initiatePayment.isPending}
									>
										{initiatePayment.isPending
											? <Loader2 className="h-4 w-4 animate-spin mr-2" />
											: null}
										Get bank details
									</Button>
								</div>
							)}
				</CardContent>
			</Card>

			<div className="grid gap-4 sm:grid-cols-2">
				<Card>
					<CardHeader className="p-4 sm:p-6">
						<CardTitle className="text-base">Buyer</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2 p-4 pt-0 text-sm sm:p-6 sm:pt-0">
						<p className="break-words font-medium">{buyerName}</p>
						{buyer?.email ? <p className="break-all text-muted-foreground">{String(buyer.email)}</p> : null}
						{buyer?.phoneNumber || buyer?.phone
							? <p className="break-all text-muted-foreground">{String(buyer.phoneNumber || buyer.phone)}</p>
							: null}
						{buyer?.buyerAddress
							? <p className="break-words text-muted-foreground">{String(buyer.buyerAddress)}</p>
							: null}
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="p-4 sm:p-6">
						<CardTitle className="text-base">Summary</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2 p-4 pt-0 text-sm sm:p-6 sm:pt-0">
						<div className="flex justify-between gap-2">
							<span className="text-muted-foreground">Items</span>
							<span>{items.length}</span>
						</div>
						{contract.deliveryCost != null
							? (
									<div className="flex justify-between gap-2">
										<span className="text-muted-foreground">Delivery</span>
										<span>{formatCurrency(Number(contract.deliveryCost) || 0, "NGN")}</span>
									</div>
								)
							: null}
						<div className="flex justify-between gap-2 font-semibold">
							<span>Total</span>
							<span>{total != null ? formatCurrency(total, "NGN") : "-"}</span>
						</div>
						{contract.description
							? (
									<p className="pt-2 text-muted-foreground border-t">
										{contract.description}
									</p>
								)
							: null}
					</CardContent>
				</Card>
			</div>

			<Card>
				<CardHeader className="p-4 sm:p-6">
					<CardTitle className="text-base">
						Items (
						{items.length}
						)
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3 p-4 pt-0 sm:p-6 sm:pt-0">
					{items.length === 0
						? <p className="text-sm text-muted-foreground">No items on this invoice.</p>
						: items.map((item, index) => {
								const qty = Number(item.quantity ?? item.productQuantity ?? 0) || 0;
								const unit = Number(item.vendorDiscountedPrice ?? item.price ?? 0) || 0;
								const line = item.totalCost != null ? Number(item.totalCost) : qty * unit;
								return (
									<div
										key={item.id ?? index}
										className="flex items-start justify-between gap-3 border-b pb-3 text-sm last:border-0 last:pb-0"
									>
										<div className="min-w-0">
											<p className="break-words font-medium">{getPublicContractItemName(item)}</p>
											<p className="text-muted-foreground">
												Qty
												{" "}
												{qty || "-"}
												{" · "}
												{formatCurrency(unit, "NGN")}
											</p>
										</div>
										<p className="shrink-0 font-semibold tabular-nums">{formatCurrency(line, "NGN")}</p>
									</div>
								);
							})}
				</CardContent>
			</Card>

			<div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
				<Button
					type="button"
					variant="outline"
					className="w-full sm:w-auto"
					onClick={() => verifyPayment.mutate(getPublicContractPaymentKey(contract))}
					disabled={verifyPayment.isPending}
				>
					{verifyPayment.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
					Check payment status
				</Button>
				{statusKey === "PENDING"
					? (
							<Button
								type="button"
								variant="destructive"
								className="w-full sm:w-auto"
								disabled={cancelInvoice.isPending}
								onClick={() => cancelInvoice.mutate(contract.id)}
							>
								{cancelInvoice.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
								Cancel invoice
							</Button>
						)
					: null}
			</div>
		</section>
	);
}

function CopyableField({
	label,
	value,
	copyValue,
	emphasis,
	mono,
	onCopy
}: {
	label: string;
	value: string;
	copyValue?: string;
	emphasis?: boolean;
	mono?: boolean;
	onCopy: (value: string, label: string) => void;
}) {
	return (
		<div>
			<p className="mb-1 text-xs text-gray-500">{label}</p>
			<div className="flex items-start justify-between gap-3">
				<p
					className={cn(
						"min-w-0 flex-1 break-all font-semibold",
						emphasis ? "text-base font-bold tracking-wide sm:text-lg sm:tracking-wider" : "text-sm",
						emphasis && label === "Amount" && "text-primary tracking-normal",
						mono && "font-mono text-xs sm:text-sm"
					)}
				>
					{value}
				</p>
				<button
					type="button"
					className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md px-2.5 text-xs font-medium text-primary hover:bg-primary/10"
					onClick={() => onCopy(copyValue ?? value, label)}
				>
					<Copy className="h-3.5 w-3.5" />
					Copy
				</button>
			</div>
		</div>
	);
}
